import React from "react";
import ReactECharts from "echarts-for-react";
import { groupSeriesXY, toTimestamp, formatDateFr } from "../../utils/echartsSeries.js";
import { YEAR_COLORS } from "../../constants.js";

const ORDRE_HORIZON = {
  2020: 0, reference: 0,
  2035: 1, "+2": 1,
  2050: 2, "+2.7": 2,
};

function valueAtDate(points, targetTime) {
  if (points.length === 0) return null;
  const first = new Date(points[0].date).getTime();
  const last = new Date(points[points.length - 1].date).getTime();
  if (targetTime < first || targetTime > last) return null;
  for (let i = 0; i < points.length - 1; i++) {
    const t1 = new Date(points[i].date).getTime();
    const t2 = new Date(points[i + 1].date).getTime();
    if (targetTime >= t1 && targetTime <= t2) {
      const frac = t2 === t1 ? 0 : (targetTime - t1) / (t2 - t1);
      return points[i].valeur + frac * (points[i + 1].valeur - points[i].valeur);
    }
  }
  return null;
}

function formatMonthLabel(monthIndex) {
  return `01/${String(monthIndex + 1).padStart(2, "0")}`;
}

export default function IrrigationJournalierChart({ data, xMax, ecart2020 }) {
  const groups = groupSeriesXY(data, {
    xKey: "date_affichage",
    groupKey: "Years",
    valueKey: "irr_moy",
  });

  const pointsByYear = {};
  groups.forEach(({ group, points }) => {
    const pointsFiltres = xMax != null ? points.filter((p) => toTimestamp(p.x) <= xMax) : points;
    pointsByYear[group] = pointsFiltres.map((p) => ({ date: p.x, valeur: p.y }));
  });

  const anneesDisponibles = Object.keys(pointsByYear).sort(
    (a, b) => (ORDRE_HORIZON[a] ?? 99) - (ORDRE_HORIZON[b] ?? 99)
  );
  const anneeBase = anneesDisponibles[0];

  const series = [];

  if (ecart2020) {
    const basePoints = pointsByYear[anneeBase] || [];
    const autresAnnees = anneesDisponibles.filter((y) => y !== anneeBase);

    autresAnnees.forEach((year) => {
      const color = YEAR_COLORS[year] || "#888";
      const diffData = basePoints
        .map((p) => {
          const t = toTimestamp(p.date);
          const val = valueAtDate(pointsByYear[year], t);
          if (val == null) return null;
          return [t, Math.round((val - p.valeur) * 100) / 100];
        })
        .filter(Boolean);
      series.push({
        name: `${year} - ${anneeBase}`,
        type: "line",
        symbol: "none",
        lineStyle: { width: 1.5, color },
        itemStyle: { color },
        data: diffData,
      });
    });
  } else {
    anneesDisponibles.forEach((group) => {
      const color = YEAR_COLORS[group] || "#888";
      series.push({
        name: String(group),
        type: "line",
        symbol: "none",
        lineStyle: { width: 1.5, color },
        itemStyle: { color },
        data: pointsByYear[group].map((p) => [toTimestamp(p.date), p.valeur]),
      });
    });
  }

  const legendData = ecart2020
    ? anneesDisponibles.filter((y) => y !== anneeBase).map((y) => `${y} - ${anneeBase}`)
    : anneesDisponibles.map((y) => String(y));

  const option = {
    tooltip: {
      trigger: "axis",
      formatter: (params) => {
        const date = formatDateFr(params[0].value[0]);
        let html = `${date}<br/>`;
        params.forEach((p) => {
          html += `${p.marker} ${p.seriesName}: ${p.value[1]} mm/j<br/>`;
        });
        return html;
      },
    },
    legend: { bottom: 0, data: legendData },
    grid: { left: 60, right: 20, top: 20, bottom: 50, containLabel: true },
    xAxis: { type: "time", max: xMax ?? undefined, axisLabel: { formatter: formatDateFr } },
    yAxis: {
      type: "value",
      name: ecart2020 ? `Écart vs ${anneeBase} (mm/j)` : "Irrigation (mm/j)",
      nameLocation: "middle",
      nameGap: 45,
    },
    series,
  };

  const autresAnneesTable = anneesDisponibles.filter((y) => y !== anneeBase);
  const pointsBase = pointsByYear[anneeBase] || [];

  const moisCandidats = Array.from({ length: 12 }, (_, m) => new Date(2025, m, 1).getTime());
  const monthsFinal = [];
  moisCandidats.forEach((t, m) => {
    const base = valueAtDate(pointsBase, t);
    if (base != null) monthsFinal.push({ m, t, base });
  });

  const ecartLignes = autresAnneesTable.map((year) => {
    const points = pointsByYear[year];
    const valeurs = monthsFinal.map(({ t, base }) => {
      const val = valueAtDate(points, t);
      if (val == null || base === 0) return null;
      return Math.round(((val - base) / base) * 1000) / 10;
    });
    return { year, valeurs };
  });

  return (
    <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
      <div style={{ flex: "2 1 0" }}>
        <ReactECharts option={option} style={{ height: 350 }} notMerge lazyUpdate />
      </div>

      {monthsFinal.length > 0 && (
        <div style={{ flex: "1 1 0", overflowX: "auto" }}>
          <table className="diagoeau-data-table diagoeau-table-centered">
            <thead>
              <tr>
                <th></th>
                <th>{anneeBase}</th>
                {ecartLignes.map((row) => (
                  <th key={row.year}>{row.year}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {monthsFinal.map(({ m, base }, i) => (
                <tr key={m}>
                  <td>{formatMonthLabel(m)}</td>
                  <td>{base.toFixed(1)} mm/j</td>
                  {ecartLignes.map((row) => {
                    const v = row.valeurs[i];
                    return (
                      <td key={row.year}>
                        {v == null ? "—" : `${v > 0 ? "+" : ""}${v.toLocaleString("fr-FR")} %`}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}